import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
// eslint-disable-next-line no-restricted-imports
import { useAsObservableSource } from 'mobx-react-lite';
import { RaceSummaryRowPlaceOnly } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummarySport/placeOnlySummary/RaceSummaryRowPlaceOnly';
import { RaceSummaryTwoColumnsList } from 'src/domains/sportsbook/webview/components/raceSummary/templates/scrollMarkets/RaceSummaryWithScroll';
import { PlaceOnlyPropsSummaryPropsType, PlaceOnlySummaryState, getSelectionByName } from './PlaceOnlySummary.state';
import { NotRunningPriceView } from 'src/domains/sportsbook/webview/components/raceSummary/utils/Utils';
import { ScrollRowWrapper } from 'src/domains/sportsbook/webview/components/raceSummary/templates/scrollMarkets/RaceSummaryWithScroll.style';
import {
    PlaceOnlySelectionCast,
    PlaceOnlyRaceSummaryCaption,
    PlaceOnlyRunnerCaption,
    PlaceOnlySelectionCaption,
} from 'src/domains/sportsbook/webview/components/raceSummary/raceSummarySport/placeOnlySummary/PlaceOnlySummary.style';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { UsersState } from 'src/domains/players/state/UsersState';

function selectionsBuilder(
    selectionName: string,
    markets: MarketModel[],
    isNonRunnerRow: boolean,
    oddsType: 'f' | 'd',
    variant: DefaultComponentVariantType | null
): Array<JSX.Element | null> | null {
    return markets.map((market) => {
        const selectionInCurrentRow = getSelectionByName(selectionName, market);
        if (isNonRunnerRow === true) {
            return (
                <NotRunningPriceView
                    key={selectionInCurrentRow?.id2.toOldId()}
                    extraPlaces={true}
                    variant={variant}
                />
            );
        }

        if (selectionInCurrentRow === null) {
            return null;
        }

        return (
            <PlaceOnlySelectionCast
                key={selectionInCurrentRow.id2.key}
                selectionId={selectionInCurrentRow.id2}
                title={oddsType === 'd' ? selectionInCurrentRow.price?.d : selectionInCurrentRow.price?.f}
                borderLeft={true}
                index={selectionInCurrentRow.id}
            />
        );
    });
}

export const PlaceOnlySummary = observer(
    'PlaceOnlySummary',
    (propsIn: PlaceOnlyPropsSummaryPropsType): JSX.Element | null => {
        const props = useAsObservableSource(propsIn);
        const common = useCommon();
        const configState = ConfigComponents.get(common).config;
        const usersState = UsersState.get(common);

        const { sportsbookVariant } = configState.componentVariants;

        const [state] = useState(() => new PlaceOnlySummaryState(props));

        const selectionsByPrice = state.selectionsByPrice;
        if (selectionsByPrice === undefined) {
            return null;
        }

        const firstSelection = selectionsByPrice[0];
        if (firstSelection === undefined) {
            return null;
        }

        const marketModel = firstSelection.getMarket();
        if (marketModel === null) {
            return null;
        }
        const oddsTypeShort = usersState.oddsFormatShort;

        const StaticList = (): JSX.Element => {
            return (
                <>
                    <PlaceOnlyRaceSummaryCaption variant={sportsbookVariant}>
                        <PlaceOnlyRunnerCaption variant={sportsbookVariant}>
                            <I18n
                                langKey='events.race-summary.runner.label'
                                defaultText='Runner'
                            />
                        </PlaceOnlyRunnerCaption>
                    </PlaceOnlyRaceSummaryCaption>

                    {selectionsByPrice.map((selection) => {
                        return (
                            <RaceSummaryRowPlaceOnly
                                key={selection.id2.key}
                                isNonRunner={state.isNonRunnerInRow(selection.name)}
                                selectionId={selection.id2}
                            />
                        );
                    })}
                </>
            );
        };

        const ScrollList = (): JSX.Element => {
            return (
                <>
                    <PlaceOnlyRaceSummaryCaption variant={sportsbookVariant}>
                        {state.marketsTerms.map((market) => (
                            <PlaceOnlySelectionCaption
                                key={market.marketId.toOldId()}
                                variant={sportsbookVariant}
                            >
                                {market.terms}
                            </PlaceOnlySelectionCaption>
                        ))}
                    </PlaceOnlyRaceSummaryCaption>
                    {selectionsByPrice.map((selection) => (
                        <ScrollRowWrapper
                            key={`price-row-${selection.id2.key}`}
                            isNonRunner={state.isNonRunnerInRow(selection.name)}
                            variant={sportsbookVariant}
                        >
                            {selectionsBuilder(
                                selection.name,
                                state.sortedMarkets,
                                state.isNonRunnerInRow(selection.name),
                                oddsTypeShort,
                                sportsbookVariant
                            )}
                        </ScrollRowWrapper>
                    ))}
                </>
            );
        };

        return (
            <RaceSummaryTwoColumnsList
                staticListElements={StaticList()}
                scrollListElements={ScrollList()}
                columnCount={state.marketsTerms.length}
            />
        );
    }
);
